import { type ChangeEvent } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { PhoneInput } from '@opendoor/atoms/phone-input';
import { Radio } from '@opendoor/atoms/radio';
import { Text } from '@opendoor/atoms/text';
import { isValidEmailAddress } from 'components/shared/emailValidation';
import {
  isAgentIdentifierValid,
  isAgentLicenseNumberValid,
  isAgentNamePairValid,
  isAgentNameValid,
  isAgentPhoneValid,
  isFullNameValid,
  type OfferPortalWizardContext,
} from 'state_machines/OfferPortalWizardMachine';

import { type ListingDetails, showsAgentLicenseNumber, showsMlsOfficeId } from './listingDetails';
import { MAX_AGENT_FIELD_LENGTH } from './offerSubmission';
import { useFieldErrors } from './useFieldErrors';
import {
  AGENT_TYPE_OPTIONS,
  CardHeading,
  FIELD_CARD,
  FIELD_INPUT,
  FIELD_LABEL,
  FieldError,
  RADIO_GROUP_AS_FIELD,
  RADIO_ROW,
  type Send,
} from './wizardFields';

const TOO_LONG_MESSAGE = `Enter at most ${MAX_AGENT_FIELD_LENGTH} characters.`;
const NAME_PAIR_TOO_LONG_MESSAGE = `First and last name together can be at most ${MAX_AGENT_FIELD_LENGTH} characters.`;

const identifierErrorMessage = (value: string, emptyMessage: string): string =>
  value.trim() === '' ? emptyMessage : TOO_LONG_MESSAGE;

const nameErrorMessage = (name: string, emptyMessage: string): string =>
  !isFullNameValid(name) ? emptyMessage : TOO_LONG_MESSAGE;

/** Step 4 of 6 of the offer wizard — the submitting agent's own details. */
const SubmittingAsStep = ({
  listing,
  context,
  send,
}: {
  listing: ListingDetails;
  context: OfferPortalWizardContext;
  send: Send;
}) => {
  const { shouldShowError, onBlur } = useFieldErrors(context.showError);
  const phoneOk = isAgentPhoneValid(context.agentPhone);
  const phoneViolation = context.fieldViolations['contactPhone'];
  const phoneInvalid = (shouldShowError('your-phone') && !phoneOk) || phoneViolation != null;
  const agentField = ({
    id,
    label,
    value,
    onValue,
    invalid,
    errorMessage,
    type = 'text',
    violation,
    optional = false,
  }: {
    id: string;
    label: string;
    value: string;
    onValue: (value: string) => void;
    invalid: boolean;
    errorMessage?: string;
    type?: 'text' | 'email';
    violation?: string;
    optional?: boolean;
  }) => {
    const showInvalid = shouldShowError(id) && invalid;
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onValue(event.target.value);
    };
    return (
      <Col className="gap-2" key={id}>
        <Row className="items-baseline justify-between gap-3">
          <Text as="label" htmlFor={id} className={FIELD_LABEL}>
            {label}
          </Text>
          {optional ? (
            <Text
              as="span"
              id={`${id}-optional`}
              className="shrink-0 text-body-xs font-normal text-od-content-tertiary"
            >
              Optional
            </Text>
          ) : null}
        </Row>
        <Input
          id={id}
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={onBlur(id)}
          size="xs"
          className={FIELD_INPUT}
          aria-label={label}
          aria-required={!optional}
          aria-describedby={optional ? `${id}-optional` : undefined}
          aria-invalid={showInvalid || violation != null}
          error={showInvalid || violation != null}
          analyticsName={`cosmos-make-offer-agent-${id}`}
        />
        {violation != null ? <FieldError>{violation}</FieldError> : null}
        {showInvalid && errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
      </Col>
    );
  };
  const handleFirstNameChange = (value: string) => {
    send({ type: 'SET_AGENT_FIRST_NAME', value });
  };
  const handleLastNameChange = (value: string) => {
    send({ type: 'SET_AGENT_LAST_NAME', value });
  };
  const handleAgentTypeChange = (value: string) => {
    send({ type: 'SET_AGENT_TYPE', value: value === 'non_mls' ? 'non_mls' : 'mls' });
  };
  const handleMlsIdChange = (value: string) => {
    send({ type: 'SET_AGENT_MLS_ID', value });
  };
  const handleMlsOfficeIdChange = (value: string) => {
    send({ type: 'SET_AGENT_MLS_OFFICE_ID', value });
  };
  const handleEmailChange = (value: string) => {
    send({ type: 'SET_AGENT_EMAIL', value });
  };
  const handlePhoneChange = (value: string) => {
    send({ type: 'SET_AGENT_PHONE', value });
  };
  const handleLicenseNumberChange = (value: string) => {
    send({ type: 'SET_AGENT_LICENSE_NUMBER', value });
  };
  return (
    <>
      <Col className={FIELD_CARD}>
        <CardHeading title="Agent details" />
        {agentField({
          id: 'your-first-name',
          label: 'First name',
          value: context.agentFirstName,
          onValue: handleFirstNameChange,
          invalid: !isAgentNameValid(context.agentFirstName),
          errorMessage: nameErrorMessage(context.agentFirstName, 'Enter your first name.'),
          violation:
            context.fieldViolations['agentFirstName'] ?? context.fieldViolations['contactName'],
        })}
        {agentField({
          id: 'your-last-name',
          label: 'Last name',
          value: context.agentLastName,
          onValue: handleLastNameChange,
          invalid:
            !isAgentNameValid(context.agentLastName) ||
            !isAgentNamePairValid(context.agentFirstName, context.agentLastName),
          errorMessage: isAgentNameValid(context.agentLastName)
            ? NAME_PAIR_TOO_LONG_MESSAGE
            : nameErrorMessage(context.agentLastName, 'Enter your last name.'),
          violation: context.fieldViolations['agentLastName'],
        })}
        {agentField({
          id: 'your-email',
          label: 'Email address',
          value: context.agentEmail,
          onValue: handleEmailChange,
          invalid: !isValidEmailAddress(context.agentEmail.trim()),
          errorMessage: 'Check if this email address is correct.',
          type: 'email',
          violation: context.fieldViolations['contactEmail'],
        })}
        <Col className="gap-2">
          <Text as="label" htmlFor="your-phone" className={FIELD_LABEL}>
            Phone number
          </Text>
          <PhoneInput
            id="your-phone"
            value={context.agentPhone}
            onValueChange={handlePhoneChange}
            onBlur={onBlur('your-phone')}
            size="xs"
            className={FIELD_INPUT}
            aria-label="Phone number"
            aria-invalid={phoneInvalid}
            error={phoneInvalid}
            analyticsName="cosmos-make-offer-agent-your-phone"
          />
          {phoneViolation != null ? <FieldError>{phoneViolation}</FieldError> : null}
          {shouldShowError('your-phone') && !phoneOk ? (
            <FieldError>Enter a valid phone number.</FieldError>
          ) : null}
        </Col>
      </Col>
      <Col className={FIELD_CARD}>
        <Radio.Group
          className={RADIO_GROUP_AS_FIELD}
          label={
            <Text as="span" className={FIELD_LABEL}>
              MLS details
            </Text>
          }
          value={context.agentType}
          onValueChange={handleAgentTypeChange}
        >
          {AGENT_TYPE_OPTIONS.map((option) => (
            <Radio.Item
              key={option.value}
              label={option.label}
              value={option.value}
              className={RADIO_ROW}
            />
          ))}
        </Radio.Group>
        {context.fieldViolations['agentType'] != null ? (
          <FieldError>{context.fieldViolations['agentType']}</FieldError>
        ) : null}
        {context.agentType === 'mls'
          ? agentField({
              id: 'agent-mls-id',
              label: 'MLS ID',
              value: context.agentMlsId,
              onValue: handleMlsIdChange,
              invalid: !isAgentIdentifierValid(context.agentMlsId),
              errorMessage: identifierErrorMessage(context.agentMlsId, 'Enter your MLS ID.'),
              violation: context.fieldViolations['agentMlsId'],
            })
          : null}
        {showsMlsOfficeId(listing, context.agentType)
          ? agentField({
              id: 'mls-office-id',
              label: 'MLS office ID',
              value: context.agentMlsOfficeId,
              onValue: handleMlsOfficeIdChange,
              invalid: !isAgentIdentifierValid(context.agentMlsOfficeId),
              errorMessage: identifierErrorMessage(
                context.agentMlsOfficeId,
                'Enter your MLS office ID.',
              ),
              violation: context.fieldViolations['agentMlsOfficeId'],
            })
          : null}
        {showsAgentLicenseNumber(context.agentType)
          ? agentField({
              id: 'agent-license-number',
              label: 'Real estate license number',
              optional: true,
              value: context.agentLicenseNumber,
              onValue: handleLicenseNumberChange,
              invalid: !isAgentLicenseNumberValid(context.agentLicenseNumber),
              errorMessage: TOO_LONG_MESSAGE,
              violation: context.fieldViolations['agentLicenseNumber'],
            })
          : null}
      </Col>
    </>
  );
};

export default SubmittingAsStep;
